// React imports
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

// Types
import { ConfiguredDiscount, SaveConfiguredDiscount, ResourcesCount } from '@spike/discount-model';
import { RootState } from 'store';
import { Service } from 'model/Service';
import AddOnService from '@spike/addon-service-model';
import { EnhancedProduct } from './AddDiscount/hooks/useDiscountForm';

// Actions
import {
    fetchConfiguredDiscountsThunk,
    saveConfiguredDiscountThunk,
    deleteConfiguredDiscountThunk,
    DiscountsStatus,
    clearDiscountError,
    clearDiscountSuccess
} from '@spike/discounts-action';
import { showErrorThunk, showSuccess } from '@spike/notifications-action';

// Hooks
import { useApiClientWrapper } from 'hooks';
import { useDiscountServices } from './AddDiscount/hooks/useDiscountServices';

// Utils
import { convertToSaveDiscount } from './Utils';

interface DiscountsContextProps {
    // Data
    discounts: ConfiguredDiscount[];
    isLoading: boolean;
    isFirstRender: boolean;
    error?: string;
    hasError: boolean;
    status: DiscountsStatus;

    // Services Data
    groomingServices: Service[];
    daycareServices: Service[];
    boardingServices: Service[];
    transportationServices: Service[];
    evaluationServices: Service[];
    addons: AddOnService[];
    products: EnhancedProduct[];
    isServicesLoading: boolean;
    resourcesCount: ResourcesCount | null;

    // Pagination
    page: number;
    size: number;
    totalPages: number;
    totalCount: number;
    hasMorePages: boolean;
    setPage: (_page: number) => void;
    setSize: (_size: number) => void;
    loadMoreDiscounts: () => void;

    // Search & Filtering
    searchText: string;
    searchTextChangeHandler: (_text: string) => void;
    selectedFilter: string; // 'active' | 'inactive' | 'all'
    filterChangeHandler: (_filter: string) => void;

    // Form State
    showAddDiscount: boolean;
    editDiscount: SaveConfiguredDiscount | null;
    originalDiscount: ConfiguredDiscount | null;
    discountMode: 'add' | 'edit' | 'view';
    openAddForm: () => void;
    openEditForm: (_discount: ConfiguredDiscount) => void;
    openViewForm: (_discount: ConfiguredDiscount) => void;
    closeForm: () => void;

    // Actions
    saveDiscount: (_discount: SaveConfiguredDiscount) => Promise<void>;
    deleteDiscount: (_discountId: number) => Promise<void>;
    activateDiscount: (_discount: ConfiguredDiscount) => Promise<void>;
    deactivateDiscount: (_discount: ConfiguredDiscount) => Promise<void>;
    refreshDiscounts: () => void;
    clearError: () => void;
    clearSuccess: () => void;

    fetchAllServices: () => void;
}

const DiscountsContext = React.createContext<DiscountsContextProps | null>(null);

interface DiscountsProviderProps {
    children: React.ReactNode;
}

export const DiscountsProvider = ({ children }: DiscountsProviderProps) => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const { discounts, resourcesCount, pagination, error, status } = useSelector((state: RootState) => state.discounts);
    // Use the custom hook to get all services data and fetching logic
    const {
        groomingServices,
        daycareServices,
        boardingServices,
        transportationServices,
        evaluationServices,
        addons,
        products,
        isLoadingData,
        fetchAllServices
    } = useDiscountServices();

    const [loading, setLoading] = React.useState({
        discounts: true,
        initialLoading: true
    });
    const [pageInfo, setPageInfo] = React.useState({
        searchText: '',
        page: 1,
        pageSize: 10,
        status: 'active'
    });

    const [showAddDiscount, setShowAddDiscount] = React.useState<boolean>(false);
    const [editDiscount, setEditDiscount] = React.useState<SaveConfiguredDiscount | null>(null);
    const [originalDiscount, setOriginalDiscount] = React.useState<ConfiguredDiscount | null>(null);
    const [discountMode, setDiscountMode] = React.useState<'add' | 'edit' | 'view'>('add');
    const [lastAction, setLastAction] = React.useState<'update' | 'activate' | 'deactivate' | 'delete' | null>(null);

    // Simple fetch function
    const fetchDiscounts = () => {
        dispatch(
            fetchConfiguredDiscountsThunk(
                apiClientWrapper,
                pageInfo.page,
                pageInfo.pageSize,
                pageInfo.searchText,
                pageInfo.status
            )
        );
    };

    // Handle errors - dispatch error thunk
    const hasError = status === DiscountsStatus.Error;
    React.useEffect(() => {
        if (hasError && error) {
            if (error.includes('name')) {
                dispatch(showErrorThunk('Automatic discount name already exists'));
            } else if (error.includes('items')) {
                dispatch(showErrorThunk('Please select at least one service'));
            } else {
                dispatch(showErrorThunk(error));
            }
        }
    }, [hasError, error, dispatch]);

    // Handle success notifications based on status and last action
    React.useEffect(() => {
        if (status === DiscountsStatus.SaveSuccess && lastAction) {
            const messages = {
                update: 'Automatic discount updated successfully',
                activate: 'Automatic discount activated successfully',
                deactivate: 'Automatic discount deactivated successfully'
            };
            if (lastAction !== 'delete') {
                dispatch(showSuccess(messages[lastAction]));
            }
            setLastAction(null);
        } else if (status === DiscountsStatus.DeleteSuccess && lastAction === 'delete') {
            dispatch(showSuccess('Automatic discount deleted successfully'));
            setLastAction(null);
        }
    }, [status, lastAction, dispatch]);

    const currentPageDiscounts = React.useMemo(() => {
        // Apply client-side filtering to handle updated data correctly
        let filtered = discounts;

        // Apply status filter
        if (pageInfo.status && pageInfo.status !== 'all') {
            const isActive = pageInfo.status === 'active';
            filtered = filtered.filter(discount => discount.active === isActive);
        }

        // Apply search filter
        if (pageInfo.searchText) {
            const searchLower = pageInfo.searchText.toLowerCase();
            filtered = filtered.filter(discount => discount.name!.toLowerCase().includes(searchLower));
        }

        return filtered;
    }, [discounts, pageInfo.status, pageInfo.searchText]);

    const hasMorePages = React.useMemo(() => {
        return pagination ? pageInfo.page < pagination.totalPages : false;
    }, [pagination, pageInfo.page]);

    // Fetch discounts when pageInfo changes
    React.useEffect(() => {
        fetchDiscounts();
    }, [pageInfo]);

    // Fetch services on mount
    React.useEffect(() => {
        fetchAllServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when save operations complete successfully
    React.useEffect(() => {
        if (status === DiscountsStatus.SaveSuccess || status === DiscountsStatus.DeleteSuccess) {
            fetchDiscounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    // Handle status changes for discounts
    React.useEffect(() => {
        if (status === DiscountsStatus.FetchSuccess || status === DiscountsStatus.Error) {
            setLoading(prev => ({ ...prev, discounts: false, initialLoading: false }));
        }
        if (status === DiscountsStatus.Fetching) {
            setLoading(prev => ({ ...prev, discounts: true }));
        }
    }, [status]);

    const saveDiscount = React.useCallback(
        (discount: SaveConfiguredDiscount): Promise<void> => {
            return dispatch(saveConfiguredDiscountThunk(apiClientWrapper, discount)) as unknown as Promise<void>;
        },
        [apiClientWrapper, dispatch]
    );

    const activateDiscount = React.useCallback(
        (discount: ConfiguredDiscount): Promise<void> => {
            setLastAction('activate');
            return dispatch(
                saveConfiguredDiscountThunk(apiClientWrapper, { id: discount.id, active: true })
            ) as unknown as Promise<void>;
        },
        [apiClientWrapper, dispatch]
    );

    const deactivateDiscount = React.useCallback(
        (discount: ConfiguredDiscount): Promise<void> => {
            setLastAction('deactivate');
            return dispatch(
                saveConfiguredDiscountThunk(apiClientWrapper, { id: discount.id, active: false })
            ) as unknown as Promise<void>;
        },
        [apiClientWrapper, dispatch]
    );

    const deleteDiscount = React.useCallback(
        (discountId: number): Promise<void> => {
            setLastAction('delete');
            return dispatch(deleteConfiguredDiscountThunk(apiClientWrapper, discountId)) as unknown as Promise<void>;
        },
        [apiClientWrapper, dispatch]
    );

    const loadMoreDiscounts = React.useCallback(() => {
        if (hasMorePages && !loading.discounts) {
            setPageInfo(prev => ({ ...prev, page: prev.page + 1 }));
        }
    }, [hasMorePages, loading.discounts]);

    const refreshDiscounts = React.useCallback(() => {
        setPageInfo(prev => ({ ...prev, page: 1 }));
    }, []);

    const clearError = React.useCallback(() => {
        dispatch(clearDiscountError());
    }, [dispatch]);

    const clearSuccess = React.useCallback(() => {
        dispatch(clearDiscountSuccess());
    }, [dispatch]);

    /*
     * ---- Event Handlers ----
     */

    const filterChangeHandler = React.useCallback((status: string) => {
        setPageInfo(prev => ({ ...prev, status, page: 1 }));
    }, []);

    const searchTextChangeHandler = useDebouncedCallback((text: string) => {
        setPageInfo(prev => ({ ...prev, searchText: text, page: 1 }));
    }, 500);

    const openAddForm = React.useCallback(() => {
        clearSuccess(); // Clear any previous SaveSuccess status
        setShowAddDiscount(true);
        setEditDiscount(null);
        setDiscountMode('add');
    }, [clearSuccess]);

    const openEditForm = React.useCallback(
        (discount: ConfiguredDiscount) => {
            clearSuccess(); // Clear any previous SaveSuccess status
            // Store the original discount for comparison when editing
            setOriginalDiscount(discount);
            // Use the Utils conversion function with services context
            const saveDiscount = convertToSaveDiscount(discount, {
                groomingServices,
                daycareServices,
                boardingServices,
                transportationServices,
                evaluationServices,
                addons,
                products
            });
            setEditDiscount(saveDiscount);
            setShowAddDiscount(false);
            setDiscountMode('edit');
        },
        [
            clearSuccess,
            groomingServices,
            daycareServices,
            boardingServices,
            transportationServices,
            evaluationServices,
            addons,
            products
        ]
    );

    const openViewForm = React.useCallback(
        (discount: ConfiguredDiscount) => {
            clearSuccess(); // Clear any previous SaveSuccess status
            // Store the original discount for comparison
            setOriginalDiscount(discount);
            // Use the Utils conversion function with services context
            const saveDiscount = convertToSaveDiscount(discount, {
                groomingServices,
                daycareServices,
                boardingServices,
                transportationServices,
                evaluationServices,
                addons,
                products
            });
            setEditDiscount(saveDiscount);
            setShowAddDiscount(false);
            setDiscountMode('view');
        },
        [
            clearSuccess,
            groomingServices,
            daycareServices,
            boardingServices,
            transportationServices,
            evaluationServices,
            addons,
            products
        ]
    );

    const closeForm = React.useCallback(() => {
        setShowAddDiscount(false);
        setEditDiscount(null);
        setOriginalDiscount(null);
        setDiscountMode('add');
    }, []);

    return (
        <DiscountsContext.Provider
            value={{
                discounts: currentPageDiscounts,
                isLoading: loading.discounts,
                isFirstRender: loading.initialLoading,
                error,
                hasError: !!error,
                status,

                groomingServices,
                daycareServices,
                boardingServices,
                transportationServices,
                evaluationServices,
                addons,
                products,
                isServicesLoading: isLoadingData,
                resourcesCount: resourcesCount ?? null,

                size: pageInfo.pageSize,
                page: pageInfo.page,
                totalPages: pagination?.totalPages ?? 1,
                totalCount: pagination?.totalCount ?? 0,
                hasMorePages,
                setSize: (size: number) => setPageInfo(prev => ({ ...prev, pageSize: size, page: 1 })),
                setPage: (page: number) => setPageInfo(prev => ({ ...prev, page })),
                loadMoreDiscounts,

                selectedFilter: pageInfo.status,
                filterChangeHandler,
                searchText: pageInfo.searchText,
                searchTextChangeHandler,

                showAddDiscount,
                editDiscount,
                originalDiscount,
                discountMode,
                openAddForm,
                openEditForm,
                openViewForm,
                closeForm,

                saveDiscount,
                deleteDiscount,
                activateDiscount,
                deactivateDiscount,
                refreshDiscounts,
                clearError,
                clearSuccess,

                fetchAllServices
            }}
        >
            {children}
        </DiscountsContext.Provider>
    );
};

export const useDiscounts = (): DiscountsContextProps => {
    const context = React.useContext(DiscountsContext);

    if (!context) {
        throw new Error('useDiscounts must be used within a DiscountsProvider');
    }

    return context;
};
